import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Paper,
  TextField,
  Typography,
  Snackbar,
  Alert,
  Stack,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { Link as RouterLink } from 'react-router-dom';
import {
  getProductTypes,
  createProductType,
  updateProductType,
  deleteProductType,
} from '../../../app/api';
import { useTranslation } from '../../../app/LanguageContext';
import { normalizeProductType } from '../private/productVariantUtils';
import ProductTypesSortableList from '../private/ProductTypesSortableList';

const ProductTypesSettingsPage = () => {
  const { translate } = useTranslation();
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [savingId, setSavingId] = useState(null);
  const [reordering, setReordering] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const loadTypes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getProductTypes({ limit: 100 });
      const list = (res.data || []).map(normalizeProductType).filter((t) => t.id);
      list.sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));
      setTypes(list);
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTypes();
  }, [loadTypes]);

  const persistOrder = async (nextTypes) => {
    await Promise.all(
      nextTypes.map((type, index) =>
        updateProductType(type.id, { name: type.name, display_order: index }),
      ),
    );
  };

  const handleReorder = async (nextTypes) => {
    setTypes(nextTypes);
    setReordering(true);
    try {
      await persistOrder(nextTypes);
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
      await loadTypes();
    } finally {
      setReordering(false);
    }
  };

  const handleAdd = async () => {
    const name = newName.trim();
    if (!name) return;
    try {
      await createProductType({ name, display_order: types.length });
      setNewName('');
      await loadTypes();
      setSnackbar({ open: true, message: translate('products.product_type_created'), severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  const handleNameChange = (typeId, name) => {
    setTypes((prev) => prev.map((t) => (t.id === typeId ? { ...t, name } : t)));
  };

  const handleSaveRow = async (type) => {
    if (!type.name.trim()) return;
    setSavingId(type.id);
    try {
      await updateProductType(type.id, { name: type.name.trim(), display_order: type.display_order ?? 0 });
      setSnackbar({ open: true, message: translate('products.product_type_updated'), severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setSavingId(null);
    }
  };

  const handleDelete = async (type) => {
    if (!window.confirm(translate('products.product_type_delete_confirm'))) return;
    try {
      await deleteProductType(type.id);
      await loadTypes();
      setSnackbar({ open: true, message: translate('products.product_type_deleted'), severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  return (
    <Box sx={{ maxWidth: 720, mx: 'auto' }}>
      <Typography variant="h5" component="h1" gutterBottom>
        {translate('products.product_types_settings_title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {translate('products.product_types_settings_hint')}{' '}
        <RouterLink to="/products">{translate('products.title')}</RouterLink>
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          {translate('products.product_type_new')}
        </Typography>
        <Stack direction="row" spacing={1}>
          <TextField
            fullWidth
            size="small"
            placeholder={translate('products.product_type_new_placeholder')}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void handleAdd();
              }
            }}
          />
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd}>
            {translate('common.add')}
          </Button>
        </Stack>
      </Paper>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {translate('products.product_types_reorder_hint')}
      </Typography>

      <ProductTypesSortableList
        types={types}
        loading={loading}
        reordering={reordering}
        savingId={savingId}
        onReorder={handleReorder}
        onNameChange={handleNameChange}
        onNameBlur={handleSaveRow}
        onDelete={handleDelete}
      />

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default ProductTypesSettingsPage;
